import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { channelIcons } from "@/constants/channelIcons";
import { cn, humanizeDuration } from "@/utils";
import { EVENT_DISPLAY_NAME } from "@/features/notification-settings/utils/eventLabels";
import { TRIGGERS } from "@/pages/Dashboard/NotificationSettings/constants/Triggers";
import type {
  GetNotificationChannels,
  GetNotificationSettings,
} from "@/server-types";
import {
  getActiveChannelsForVariant,
  pickAppointmentVariant,
  type VariantCandidate,
} from "@/utils/notification-variant";
import { i18n } from "@lingui/core";
import React from "react";

export type NotificationSettingToggle = { id: number; enabled: boolean };

type NotificationSettingDetails =
  GetNotificationSettings["response"]["settings"][number];

type NotificationChannels = GetNotificationChannels["response"] | undefined;

// Renders one icon per channel that will fire for this appointment context.
// Picks the most-specific variant (service + location) and lists its active
// channels — email and fallback channels can coexist (the resolver fans email
// out independently of the sms/viber/whatsapp chain), so we never collapse
// them. Falls back to the tenant-level channel list when a setting has no
// variants (defensive — every setting gets a default variant during backfill).
export function renderChannelIcons(
  settingDetails:
    | {
        notificationType?: string;
        variants?: VariantCandidate[] | null;
      }
    | null
    | undefined,
  selectedServices: { serviceId: number | string }[],
  selectedLocationId: number | null,
  notificationChannels: NotificationChannels,
): React.ReactNode {
  const variants = settingDetails?.variants ?? [];
  if (variants.length > 0) {
    // Union across every selected service, matching the rule that decides
    // whether the setting is listed at all — a setting can fire through the
    // second service while the first resolves to a variant with no channels.
    const serviceIds: (number | null)[] =
      selectedServices.length > 0
        ? selectedServices.map((service) =>
            service?.serviceId != null ? Number(service.serviceId) : null,
          )
        : [null];

    const channels = new Set(
      serviceIds.flatMap((serviceId) =>
        getActiveChannelsForVariant(
          pickAppointmentVariant(variants, {
            serviceId,
            locationId: selectedLocationId ?? null,
          }),
        ).map((c) => c.channel),
      ),
    );

    // A setting that has variants is fully described by them: when no variant
    // has active channels for this service/location the notification will not
    // fire, so render nothing rather than falling through to the tenant-wide
    // channel list (which would wrongly imply a message is sent at a location
    // where the variant disables it).
    return channels.size > 0
      ? [...channels].map((channel) => (
          <span key={channel} className="flex items-center">
            {channelIcons[channel]}
          </span>
        ))
      : null;
  }

  // Legacy fallback: settings predating the variant backfill.
  const notificationType = settingDetails?.notificationType || "";
  if (notificationType === "fallbackFlow") {
    return notificationChannels?.channels.map((c) => (
      <span key={c.notificationType} className="flex items-center">
        {channelIcons[c.notificationType]}
      </span>
    ));
  }
  if (notificationType === "email") {
    return channelIcons.email;
  }
  return null;
}

export function getNotificationSettingLabel(
  settingDetails: NotificationSettingDetails | undefined,
): string {
  const trigger = settingDetails?.trigger || "";
  const event = settingDetails?.event;

  return (
    TRIGGERS().find((t) => t.trigger === trigger)?.label ||
    (event ? i18n._(EVENT_DISPLAY_NAME[event]) : "") ||
    ""
  );
}

export function getNotificationOffsetLabel(
  settingDetails: NotificationSettingDetails | undefined,
): string | undefined {
  const trigger = settingDetails?.trigger || "";
  const event = settingDetails?.event;

  const hasTimeOffset =
    ["cronBeforeAppointment", "cronAfterAppointment"].includes(trigger) ||
    (!!event && (settingDetails?.timeOffset || 0) > 0);

  return hasTimeOffset
    ? humanizeDuration((settingDetails?.timeOffset || 0) * 60000)
    : undefined;
}

export function NotificationSwitchRow({
  label,
  description,
  scheduledTime,
  icons,
  isSelected,
  onValueChange,
}: {
  label: string;
  description?: string;
  scheduledTime?: string | null;
  icons?: React.ReactNode;
  isSelected: boolean;
  onValueChange: (enabled: boolean) => void;
}) {
  return (
    <LimeSwitch
      size="sm"
      isSelected={isSelected}
      onValueChange={onValueChange}
      classNames={{ label: "w-full" }}
      isCompact
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="text-sm leading-5">{label}</span>
          {description ? (
            <span className="text-default-500 text-xs">{description}</span>
          ) : null}
          {scheduledTime ? (
            <span className="text-default-400 text-xs">{scheduledTime}</span>
          ) : null}
        </div>
        <div
          className={cn(
            // channelIcons are hardcoded to 30px, which dwarfs the row label
            "flex shrink-0 items-center gap-1.5 transition-opacity [&_svg]:size-6",
            !isSelected && "opacity-40",
          )}
        >
          {icons}
        </div>
      </div>
    </LimeSwitch>
  );
}

export function NotificationSettingsSwitchList({
  values,
  onToggle,
  settings,
  selectedServices,
  locationId,
  notificationChannels,
}: {
  values: NotificationSettingToggle[];
  onToggle: (settingId: number, enabled: boolean) => void;
  settings: NotificationSettingDetails[] | undefined;
  selectedServices: { serviceId: number | string }[];
  locationId: number | null;
  notificationChannels: NotificationChannels;
}) {
  return (
    <div className="flex flex-col gap-1">
      {values.map((setting) => {
        const settingDetails = settings?.find((s) => s.id === setting.id);

        return (
          <NotificationSwitchRow
            key={setting.id}
            label={getNotificationSettingLabel(settingDetails)}
            description={getNotificationOffsetLabel(settingDetails)}
            icons={renderChannelIcons(
              settingDetails,
              selectedServices,
              locationId,
              notificationChannels,
            )}
            isSelected={setting.enabled}
            onValueChange={(enabled) => onToggle(setting.id, enabled)}
          />
        );
      })}
    </div>
  );
}
